/**
 * RegionFilter plugin - multi-select filter for regions (voivodeships).
 *
 * This is a conditional filter (DD-6): Component returns null when loaded + no data.
 * Shows skeleton during isLoading to prevent layout shift.
 *
 * URL param: region
 * API param: region (same as URL)
 * SavedView field: regions
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 4, DD-6)
 */

import React, { useMemo } from "react";
import { FilterPlugin, FilterComponentProps } from "../types";
import { SavedViewFilters } from "../../../../../services/grantsApi";
import MultiSelectDropdown from "../../../../common/MultiSelectDropdown";
import { getRegionLabel } from "../../../../../constants/regions";

/**
 * Region filter React component.
 * Conditional: returns null when loaded + no data (DD-6).
 * Shows skeleton during isLoading to prevent layout shift.
 */
const RegionFilterComponent = React.memo(function RegionFilterComponent(
  props: FilterComponentProps<string[]>
) {
  const { value, onChange, facets, isLoading } = props;

  const regions = facets?.regions as string[] | undefined;

  const options = useMemo(
    () =>
      (regions ?? []).map((region) => ({
        value: region,
        label: getRegionLabel(region),
      })),
    [regions]
  );

  // DD-6: Show skeleton during loading to prevent layout shift
  if (isLoading) {
    return React.createElement("div", {
      className: "w-64 h-10 bg-neutral-200 rounded-md animate-pulse",
    });
  }

  // DD-6: Conditional rendering - hide when no regions available and none selected
  if (!regions && value.length === 0) {
    return null;
  }

  return React.createElement(
    "div",
    { className: "w-64 relative" },
    React.createElement(MultiSelectDropdown, {
      options,
      selectedValues: value,
      onChange,
      placeholder: "Wszystkie regiony",
      ariaLabel: "Filtruj według regionu",
    })
  );
});

export const regionFilter: FilterPlugin<string[]> = {
  key: "region",
  label: "Region",
  placement: "main",
  order: 4,
  defaultValue: [],
  Component: RegionFilterComponent,

  isActive(value: string[]): boolean {
    return value.length > 0;
  },

  toUrlParams(value: string[], params: URLSearchParams): void {
    value.forEach((r) => params.append("region", r));
  },

  fromUrlParams(params: URLSearchParams): string[] {
    return params.getAll("region");
  },

  toSavedView(value: string[], filters: Partial<SavedViewFilters>): void {
    filters.regions = value;
  },

  fromSavedView(filters: SavedViewFilters): string[] {
    return filters.regions ?? [];
  },

  toApiParams(value: string[], params: URLSearchParams): void {
    value.forEach((r) => params.append("region", r));
  },
};
